import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Select } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleRight, faFileImport, faPen, faPills, faPlus, faTrash, faXmark } from '@fortawesome/free-solid-svg-icons';
import {
    createGenericSig, deleteGenericSig, deleteGenericSigs, getGenericSigGenerics, getGenericSigs,
    getGenericSigsBySig, getGenericSigSigs, importGenericSigs, searchGenerics, tagGenericSig,
    updateGenericSig, updateGenericSigs,
} from '../../../service/api/generic-sig';
import {
    buildDuration, composeSig, formatDuration, genericSigToInfo, getSigTypeWord,
} from '../../../utils/common-funtions/medicine-sig';
import { dosageForms } from '../../../utils/static-data/data';
import { showErrorToast, showSuccessToast } from '../../../utils/toaster-alert';
import CustomModal from '../../common/modal/modal';
import SigFields from '../../prescription/main-prescription/medicine/medicine-sig-form/sig-fields';
import '../../common/group-manager/group-manager.scss';
import '../../prescription/main-prescription/medicine/medicine-sig-form/medicine-sig-form.scss';
import './generic-sig-manager.scss';

const EMPTY_INFO = { take: '', route: '', frequency: '', instruction: '', duration: '', duration_time: '' };

const SOURCE_LABELS = { learned: 'learned', imported: 'imported' };

// Two ways into the same data: pick a generic and manage its SIGs, or pick a
// SIG and manage the generics it is tagged to.
const VIEWS = [
    { key: 'generic', label: 'By generic' },
    { key: 'sig', label: 'By SIG' },
];

// A SIG tagged to several generics is stored as one preset per generic; presets
// that read the same (form + SIG text + duration) are that one SIG.
const sigGroupKey = (preset) =>
    [preset?.form || '', (preset?.sig || '').trim(), formatDuration(preset?.duration).trim()].join('\u0001');

const sigFieldsOf = (group) => ({
    form: group?.form || '',
    take: group?.take || '',
    route: group?.route || '',
    frequency: group?.frequency || '',
    instruction: group?.instruction || '',
    sig: group?.sig || '',
    duration: group?.duration || '',
});

// what identifies a SIG on the server
const sigIdentityOf = (group) => ({
    form: group?.form || '',
    sig: (group?.sig || '').trim(),
    duration: group?.duration || '',
});

// the server returns at most this many distinct SIGs (most used first)
const SIG_LIST_LIMIT = 300;

const formLabel = (preset) => `${preset?.form || 'Any form'}${preset?.power ? ` · ${preset.power}` : ''}`;

// Master/detail management of a doctor's SIG presets. "By generic": generics on the
// left (only values from the master medicine list can be added), the selected
// generic's SIGs + editor on the right. "By SIG": the doctor's distinct SIGs on the
// left, the generics tagged to the selected one on the right.
//
// Nothing here loads all of a doctor's presets: the lists are server summaries
// (generics with counts, distinct SIGs) and details are fetched for the one
// generic / SIG that is selected.
const GenericSigManager = ({ doctorId, onCountChange }) => {
    const { user } = useSelector((state) => state.auth);
    const language = user?.user?.details?.config?.language || 'english';

    const [view, setView] = useState(VIEWS[0].key);
    const [genericsList, setGenericsList] = useState([]); // [{ generic, count }]
    const [addedGenerics, setAddedGenerics] = useState([]); // picked, no preset saved yet
    const [selectedGeneric, setSelectedGeneric] = useState('');
    const [selectedPresets, setSelectedPresets] = useState([]); // the selected generic's SIGs
    const [filter, setFilter] = useState('');
    const [reuseSigs, setReuseSigs] = useState([]); // "use an existing SIG" options

    const [genericOptions, setGenericOptions] = useState([]);
    const [genericQuery, setGenericQuery] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const searchTimeoutRef = useRef(null);

    // editor (by generic): null = closed, {} = new preset, preset = editing it
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState('');
    const [power, setPower] = useState('');
    const [isImporting, setIsImporting] = useState(false);
    const [info, setInfo] = useState(EMPTY_INFO);
    const [sig, setSig] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    // { title, text, confirmLabel, onConfirm }
    const [confirm, setConfirm] = useState(null);

    // by SIG
    const [sigList, setSigList] = useState([]); // distinct SIGs, most used first
    const [selectedSig, setSelectedSig] = useState(null); // one of sigList (or a just-saved SIG)
    const [sigRows, setSigRows] = useState([]); // the selected SIG's presets = its generic tags
    const [sigFilter, setSigFilter] = useState('');
    const [sigEditing, setSigEditing] = useState(null); // null | 'new' | 'edit'
    const [newSigGenerics, setNewSigGenerics] = useState([]);
    const sigFilterTimeoutRef = useRef(null);
    const reuseTimeoutRef = useRef(null);
    // newest request wins when the selection changes quickly
    const presetsRequestRef = useRef(0);
    const sigListRequestRef = useRef(0);
    const sigRowsRequestRef = useRef(0);

    // ---- loading: summaries + the selected item's details ----
    const loadGenerics = async () => {
        if (!doctorId) return;
        const res = await getGenericSigGenerics(doctorId).catch(() => null);
        if (res?.status === 200) {
            const list = res.data || [];
            setGenericsList(list);
            onCountChange?.(list.reduce((total, g) => total + (g.count || 0), 0));
        }
    };

    const loadGenericPresets = async (generic) => {
        const requestId = ++presetsRequestRef.current;
        if (!doctorId || !generic) {
            setSelectedPresets([]);
            return;
        }
        const res = await getGenericSigs(doctorId, { generic }).catch(() => null);
        if (requestId === presetsRequestRef.current && res?.status === 200) setSelectedPresets(res.data || []);
    };

    const withKeys = (sigs) => (sigs || []).map((item) => ({ ...item, key: sigGroupKey(item) }));

    const loadSigList = async (search) => {
        if (!doctorId) return;
        const requestId = ++sigListRequestRef.current;
        const res = await getGenericSigSigs(doctorId, (search || '').trim()).catch(() => null);
        if (requestId === sigListRequestRef.current && res?.status === 200) setSigList(withKeys(res.data));
    };

    const loadSigRows = async (group) => {
        const requestId = ++sigRowsRequestRef.current;
        if (!doctorId || !group?.sig) {
            setSigRows([]);
            return;
        }
        const res = await getGenericSigsBySig(doctorId, sigIdentityOf(group)).catch(() => null);
        if (requestId !== sigRowsRequestRef.current || res?.status !== 200) return;
        const rows = res.data || [];
        setSigRows(rows);
        // its last generic was removed -> the SIG is gone
        if (!rows.length) setSelectedSig(null);
    };

    // after a change in "By generic"
    const refreshGenericView = (generic = selectedGeneric) => {
        loadGenerics();
        loadGenericPresets(generic);
    };

    // after a change in "By SIG"
    const refreshSigView = (group = selectedSig) => {
        loadGenerics();
        loadSigList(sigFilter);
        loadSigRows(group);
    };

    useEffect(() => {
        loadGenerics();
        loadSigList('');
        setSelectedGeneric('');
        setSelectedPresets([]);
        setAddedGenerics([]);
        setEditing(null);
        setSelectedSig(null);
        setSigRows([]);
        setSigFilter('');
        setSigEditing(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [doctorId]);

    // [{ generic, count }] sorted by name: saved generics + freshly added empty ones
    const generics = useMemo(() => {
        const known = new Set(genericsList.map((g) => g.generic));
        return [
            ...genericsList,
            ...addedGenerics.filter((generic) => !known.has(generic)).map((generic) => ({ generic, count: 0 })),
        ].sort((a, b) => a.generic.localeCompare(b.generic));
    }, [genericsList, addedGenerics]);

    const visibleGenerics = filter.trim()
        ? generics.filter((g) => g.generic.toLowerCase().includes(filter.trim().toLowerCase()))
        : generics;

    // the selected SIG's numbers come from its loaded presets
    const sigGenericCount = new Set(sigRows.map((row) => row.generic)).size;
    const sigUsage = sigRows.reduce((total, row) => total + (row.usageCount || 0), 0);

    // SIGs the selected generic doesn't have yet ("use an existing SIG")
    const reusableSigOptions = useMemo(() => {
        const taken = new Set(selectedPresets.map(sigGroupKey));
        return reuseSigs
            .filter((group) => !taken.has(group.key))
            .map((group) => ({
                value: group.key,
                label: `${group.sig}${group.duration ? ` · ${formatDuration(group.duration)}` : ''}${group.form ? ` — ${group.form}` : ''}`,
            }));
    }, [reuseSigs, selectedPresets]);

    const loadReuseSigs = async (search) => {
        if (!doctorId) return;
        const res = await getGenericSigSigs(doctorId, (search || '').trim()).catch(() => null);
        if (res?.status === 200) setReuseSigs(withKeys(res.data));
    };

    const handleReuseSearch = (text) => {
        clearTimeout(reuseTimeoutRef.current);
        reuseTimeoutRef.current = setTimeout(() => loadReuseSigs(text), 400);
    };

    const errorMessage = (error, fallback) => error?.response?.data?.errors?.[0]?.msg || fallback;

    const switchView = (nextView) => {
        setView(nextView);
        setEditing(null);
        setSigEditing(null);
        // the other view may have changed things since this one was last shown
        if (nextView === 'sig') refreshSigView();
        else refreshGenericView();
    };

    // ---- master generic picker ----
    const handleGenericSearch = (text) => {
        setGenericQuery(text);
        clearTimeout(searchTimeoutRef.current);
        if (!text?.trim()) {
            setGenericOptions([]);
            return;
        }
        searchTimeoutRef.current = setTimeout(async () => {
            setIsSearching(true);
            const res = await searchGenerics(text.trim()).catch(() => null);
            if (res?.status === 200) setGenericOptions(res.data || []);
            setIsSearching(false);
        }, 400);
    };

    const genericNotFound = genericQuery.trim() ? (isSearching ? 'Searching…' : 'No generic found') : 'Type to search generics';

    const resetGenericSearch = () => {
        setGenericQuery('');
        setGenericOptions([]);
    };

    const handleAddGeneric = (generic) => {
        if (!generic) return;
        if (!generics.some((g) => g.generic === generic)) setAddedGenerics((prev) => [...prev, generic]);
        resetGenericSearch();
        selectGeneric(generic);
        // nothing saved for it yet -> go straight to the editor
        if (!generics.some((g) => g.generic === generic && g.count > 0)) openEditor({});
    };

    const selectGeneric = (generic) => {
        setSelectedGeneric(generic);
        setEditing(null);
        setSelectedPresets([]);
        loadGenericPresets(generic);
    };

    // ---- SIG editor (shared by both views) ----
    const fillEditor = (source) => {
        setForm(source?.form || '');
        setPower(source?.power || '');
        setInfo(source?.sig ? genericSigToInfo(source) : EMPTY_INFO);
        setSig(source?.sig || '');
    };

    const openEditor = (preset) => {
        setEditing(preset);
        fillEditor(preset);
    };

    const recomposeSig = (nextInfo, nextForm) => {
        setSig(composeSig({
            take: nextInfo.take,
            typeWord: getSigTypeWord(nextForm, language),
            route: nextInfo.route,
            frequency: nextInfo.frequency,
            instruction: nextInfo.instruction,
        }));
    };

    // Same behaviour as the medicine SIG form: editing a dosing field rebuilds the SIG.
    const handleFieldChange = (key, value) => {
        const nextInfo = { ...info, [key]: value };
        if (value == 'চলবে' || value == 'continue') nextInfo.duration = value;
        setInfo(nextInfo);
        if (['take', 'route', 'frequency', 'instruction'].includes(key)) recomposeSig(nextInfo, form);
    };

    const handleFormChange = (value) => {
        setForm(value);
        if (info.take) recomposeSig(info, value);
    };

    const editorPayload = () => ({
        form,
        take: info.take,
        route: info.route,
        frequency: info.frequency,
        instruction: info.instruction,
        sig,
        duration: buildDuration(info),
    });

    // ---- by generic ----
    const handleSave = async () => {
        if (!sig.trim() || !selectedGeneric) return;
        const payload = { ...editorPayload(), generic: selectedGeneric, power };
        setIsSaving(true);
        try {
            const res = editing?._id
                ? await updateGenericSig(editing._id, doctorId, payload)
                : await createGenericSig({ ...payload, doctorId });
            if (res?.status === 200) {
                showSuccessToast(editing?._id ? 'SIG updated' : 'SIG saved');
                setEditing(null);
                refreshGenericView();
            }
        } catch (error) {
            showErrorToast(errorMessage(error, 'Could not save the SIG'));
        } finally {
            setIsSaving(false);
        }
    };

    // Attach a SIG the doctor already has (for another generic) to this generic
    const handleUseExistingSig = async (key) => {
        const group = reuseSigs.find((g) => g.key === key);
        if (!group || !selectedGeneric) return;
        try {
            const res = await tagGenericSig({ ...sigFieldsOf(group), doctorId, generics: [selectedGeneric] });
            if (res?.status === 200) {
                showSuccessToast('SIG added');
                refreshGenericView();
            }
        } catch (error) {
            showErrorToast(errorMessage(error, 'Could not add the SIG'));
        }
    };

    // Seed presets from My Drug List + past visits (server skips ones that exist)
    const handleImport = async () => {
        setIsImporting(true);
        try {
            const res = await importGenericSigs(doctorId);
            if (res?.status === 200) {
                const created = res.data?.created || 0;
                showSuccessToast(created ? `${created} SIG${created > 1 ? 's' : ''} imported` : 'Nothing new to import');
                if (view === 'sig') refreshSigView();
                else refreshGenericView();
            }
        } catch (error) {
            showErrorToast(errorMessage(error, 'Could not import SIGs'));
        } finally {
            setIsImporting(false);
        }
    };

    const handleDelete = async (preset) => {
        const res = await deleteGenericSig(preset?._id, doctorId).catch(() => null);
        if (res?.status === 200) {
            showSuccessToast('SIG deleted');
            if (editing?._id === preset?._id) setEditing(null);
            refreshGenericView();
        } else {
            showErrorToast('Could not delete the SIG');
        }
    };

    // ---- by SIG ----
    const handleSigFilter = (text) => {
        setSigFilter(text);
        clearTimeout(sigFilterTimeoutRef.current);
        sigFilterTimeoutRef.current = setTimeout(() => loadSigList(text), 400);
    };

    const selectSigGroup = (group) => {
        setSelectedSig(group);
        setSigEditing(null);
        setSigRows([]);
        resetGenericSearch();
        loadSigRows(group);
    };

    const openNewSig = () => {
        setSigEditing('new');
        setSelectedSig(null);
        setSigRows([]);
        setNewSigGenerics([]);
        resetGenericSearch();
        fillEditor(null);
    };

    const openEditSig = (group) => {
        setSigEditing('edit');
        fillEditor(group);
    };

    const handleSaveSig = async () => {
        if (!sig.trim()) return;
        const payload = editorPayload();
        setIsSaving(true);
        try {
            if (sigEditing === 'new') {
                if (!newSigGenerics.length) return;
                const res = await tagGenericSig({ ...payload, doctorId, generics: newSigGenerics });
                if (res?.status !== 200) return;
                const created = res.data?.created?.length || 0;
                const rejected = res.data?.rejected || [];
                if (rejected.length) showErrorToast(`Not in the medicine list: ${rejected.join(', ')}`, 3000);
                showSuccessToast(created ? `SIG saved for ${created} generic${created > 1 ? 's' : ''}` : 'Those generics already have this SIG');
            } else {
                if (!selectedSig) return;
                // the server finds every preset of this SIG itself
                const res = await updateGenericSigs(doctorId, sigIdentityOf(selectedSig), payload);
                if (res?.status !== 200) return;
                const updated = res.data?.updated || 0;
                showSuccessToast(`SIG updated for ${updated} generic${updated === 1 ? '' : 's'}`);
            }
            const saved = { ...payload, sig: payload.sig.trim(), key: sigGroupKey(payload) };
            setSelectedSig(saved);
            setSigEditing(null);
            refreshSigView(saved);
        } catch (error) {
            showErrorToast(errorMessage(error, 'Could not save the SIG'));
        } finally {
            setIsSaving(false);
        }
    };

    const handleTagGeneric = async (generic) => {
        if (!generic || !selectedSig) return;
        resetGenericSearch();
        try {
            const res = await tagGenericSig({ ...sigFieldsOf(selectedSig), doctorId, generics: [generic] });
            if (res?.status === 200) {
                showSuccessToast(`Tagged to ${generic}`);
                refreshSigView();
            }
        } catch (error) {
            showErrorToast(errorMessage(error, 'Could not tag the generic'));
        }
    };

    const handleUntag = async (row) => {
        const res = await deleteGenericSig(row?._id, doctorId).catch(() => null);
        if (res?.status === 200) {
            showSuccessToast(`Removed from ${row.generic}`);
            refreshSigView();
        } else {
            showErrorToast('Could not remove the generic');
        }
    };

    const handleDeleteSigGroup = async (group) => {
        const res = await deleteGenericSigs(doctorId, sigIdentityOf(group)).catch(() => null);
        if (res?.status === 200) {
            showSuccessToast('SIG deleted');
            setSelectedSig(null);
            setSigRows([]);
            setSigEditing(null);
            refreshSigView(null);
        } else {
            showErrorToast('Could not delete the SIG');
        }
    };

    // Form / (strength) / dosing fields + Cancel/Save. Strength is per generic, so
    // only the by-generic editor shows it.
    const renderEditor = ({ withPower, saveLabel, canSave, onSave, onCancel }) => (
        <div className="generic-sig-settings__editor medicine-sig-from-section">
            <div className="custom-form">
                <div className="row px-2">
                    <div className="col-12 m-0 p-0 individual-input d-flex">
                        <label className="form-label mt-1">Form:</label>
                        <select value={form} onChange={(e) => handleFormChange(e.target.value)}>
                            <option value="">Any form</option>
                            {dosageForms.map((dosageForm) => (
                                <option key={dosageForm} value={dosageForm}>{dosageForm}</option>
                            ))}
                        </select>
                    </div>
                    {withPower && (
                        <div className="col-12 m-0 p-0 individual-input d-flex">
                            <label className="form-label mt-1">Strength:</label>
                            <input className="form-control input w-50" autoComplete="off" type="text"
                                value={power} placeholder="Any strength (e.g. 500 mg)"
                                onChange={(e) => setPower(e.target.value)}
                            />
                        </div>
                    )}
                    <SigFields
                        info={info}
                        sig={sig}
                        language={language}
                        onFieldChange={handleFieldChange}
                        onSigChange={setSig}
                        onDurationPick={(option) => setInfo({ ...info, duration: option.count, duration_time: option.unit })}
                    />
                </div>
            </div>
            <div className="generic-sig-settings__editor-actions">
                <button type="button" onClick={onCancel}>Cancel</button>
                <button type="button" className="is-primary" onClick={onSave} disabled={!canSave || isSaving}>
                    {saveLabel}
                </button>
            </div>
        </div>
    );

    const importButton = (
        <button type="button" className="generic-sig-settings__import-btn" onClick={handleImport} disabled={isImporting || !doctorId}>
            <FontAwesomeIcon icon={faFileImport} /> {isImporting ? 'Importing…' : 'Import from my prescriptions'}
        </button>
    );

    return (
        <>
            {/* view switch */}
            <div className="groups-settings__tabs">
                {VIEWS.map((v) => (
                    <button
                        key={v.key}
                        type="button"
                        className={`groups-settings__tab ${view === v.key ? 'is-active' : ''}`}
                        onClick={() => switchView(v.key)}
                    >
                        {v.label}
                    </button>
                ))}
            </div>

            {view === 'generic' ? (
                <div className="groups-settings__layout">
                    {/* LEFT: generics */}
                    <div className="groups-settings__left">
                        {importButton}
                        <div className="generic-sig-settings__picker">
                            <Select
                                showSearch
                                size="large"
                                value={null}
                                placeholder="Add a generic from the medicine list…"
                                filterOption={false}
                                searchValue={genericQuery}
                                onSearch={handleGenericSearch}
                                onChange={handleAddGeneric}
                                options={genericOptions.map((generic) => ({ value: generic, label: generic }))}
                                notFoundContent={genericNotFound}
                            />
                        </div>

                        {generics.length > 8 && (
                            <input
                                className="generic-sig-settings__filter"
                                placeholder="Filter my generics"
                                value={filter}
                                onChange={(e) => setFilter(e.target.value)}
                            />
                        )}

                        {generics.length === 0 ? (
                            <div className="groups-settings__empty">
                                <FontAwesomeIcon icon={faPills} />
                                <span>No generic SIGs yet. Import them from your prescriptions, or add a generic above.</span>
                            </div>
                        ) : (
                            <div className="groups-settings__list generic-sig-settings__list">
                                {visibleGenerics.map(({ generic, count }) => (
                                    <div
                                        key={generic}
                                        className={`groups-settings__row ${selectedGeneric === generic ? 'is-selected' : ''}`}
                                        onClick={() => selectGeneric(generic)}
                                    >
                                        <span className="groups-settings__name" title={generic}>{generic}</span>
                                        <div className="groups-settings__actions">
                                            <span className="groups-settings__count">{count}</span>
                                            <FontAwesomeIcon className="groups-settings__chevron" icon={faAngleRight} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* RIGHT: selected generic's SIGs */}
                    <div className="groups-settings__detail">
                        {!selectedGeneric ? (
                            <div className="groups-settings__empty groups-settings__detail-empty">
                                <FontAwesomeIcon icon={faPills} />
                                <span>Select a generic to configure its SIGs.</span>
                            </div>
                        ) : (
                            <>
                                <div className="groups-settings__detail-head">
                                    <h3>SIGs for “{selectedGeneric}”</h3>
                                    <span className="groups-settings__count">{selectedPresets.length}</span>
                                    {!editing && (
                                        <button type="button" className="generic-sig-settings__add-btn" onClick={() => openEditor({})}>
                                            <FontAwesomeIcon icon={faPlus} /> Add SIG
                                        </button>
                                    )}
                                </div>

                                {/* reuse a SIG saved for another generic instead of typing it again */}
                                {!editing && (
                                    <div className="generic-sig-settings__picker">
                                        <Select
                                            showSearch
                                            size="large"
                                            value={null}
                                            placeholder="…or use one of my existing SIGs"
                                            filterOption={false}
                                            onFocus={() => loadReuseSigs('')}
                                            onSearch={handleReuseSearch}
                                            onChange={handleUseExistingSig}
                                            options={reusableSigOptions}
                                            notFoundContent="No other SIGs found"
                                        />
                                    </div>
                                )}

                                {editing && renderEditor({
                                    withPower: true,
                                    saveLabel: editing?._id ? 'Update' : 'Save',
                                    canSave: !!sig.trim(),
                                    onSave: handleSave,
                                    onCancel: () => setEditing(null),
                                })}

                                {selectedPresets.length === 0 && !editing ? (
                                    <div className="groups-settings__empty">
                                        <FontAwesomeIcon icon={faPills} />
                                        <span>No SIGs for this generic yet.</span>
                                    </div>
                                ) : (
                                    <div className="groups-settings__advice-list">
                                        {selectedPresets.map((preset) => (
                                            <div key={preset._id} className={`generic-sig-settings__card ${editing?._id === preset._id ? 'is-editing' : ''}`}>
                                                <div className="generic-sig-settings__card-body">
                                                    <div className="generic-sig-settings__card-meta">
                                                        <span className="generic-sig-settings__form" title={formLabel(preset)}>{formLabel(preset)}</span>
                                                        {SOURCE_LABELS[preset.source] && <span className="generic-sig-settings__lang">{SOURCE_LABELS[preset.source]}</span>}
                                                        {preset.language === 'bangla' && <span className="generic-sig-settings__lang">বাংলা</span>}
                                                        {preset.usageCount > 0 && <span className="generic-sig-settings__usage">used {preset.usageCount}×</span>}
                                                    </div>
                                                    <div className="generic-sig-settings__sig">{preset.sig}</div>
                                                    {preset.duration && <div className="generic-sig-settings__duration">Duration: {formatDuration(preset.duration)}</div>}
                                                </div>
                                                <div className="groups-settings__actions" style={{ opacity: 1 }}>
                                                    <button type="button" className="groups-settings__icon-btn" title="Edit" onClick={() => openEditor(preset)}>
                                                        <FontAwesomeIcon icon={faPen} />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="groups-settings__icon-btn is-danger"
                                                        title="Delete"
                                                        onClick={() => setConfirm({
                                                            title: 'Delete SIG?',
                                                            text: `“${preset.sig}” will be removed from ${preset.generic}. This can’t be undone.`,
                                                            confirmLabel: 'Delete',
                                                            onConfirm: () => handleDelete(preset),
                                                        })}
                                                    >
                                                        <FontAwesomeIcon icon={faTrash} />
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            ) : (
                <div className="groups-settings__layout">
                    {/* LEFT: the doctor's distinct SIGs */}
                    <div className="groups-settings__left">
                        {importButton}
                        <button type="button" className="generic-sig-settings__new-btn" onClick={openNewSig}>
                            <FontAwesomeIcon icon={faPlus} /> New SIG
                        </button>

                        <input
                            className="generic-sig-settings__filter"
                            placeholder="Search my SIGs"
                            value={sigFilter}
                            onChange={(e) => handleSigFilter(e.target.value)}
                        />

                        {sigList.length === 0 ? (
                            <div className="groups-settings__empty">
                                <FontAwesomeIcon icon={faPills} />
                                <span>{sigFilter.trim() ? 'No SIG matches that search.' : 'No SIGs yet. Import them from your prescriptions, or create one above.'}</span>
                            </div>
                        ) : (
                            <div className="groups-settings__list generic-sig-settings__list">
                                {sigList.map((group) => (
                                    <div
                                        key={group.key}
                                        className={`groups-settings__row generic-sig-settings__sig-row ${selectedSig?.key === group.key ? 'is-selected' : ''}`}
                                        onClick={() => selectSigGroup(group)}
                                    >
                                        <div className="generic-sig-settings__sig-row-text">
                                            <span className="groups-settings__name" title={group.sig}>{group.sig}</span>
                                            <small>{group.form || 'Any form'}{group.duration ? ` · ${formatDuration(group.duration)}` : ''}</small>
                                        </div>
                                        <div className="groups-settings__actions">
                                            <span className="groups-settings__count" title="Generics tagged">{group.genericCount}</span>
                                            <FontAwesomeIcon className="groups-settings__chevron" icon={faAngleRight} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                        {sigList.length >= SIG_LIST_LIMIT && (
                            <p className="generic-sig-settings__hint">Showing your {SIG_LIST_LIMIT} most used SIGs — search to find others.</p>
                        )}
                    </div>

                    {/* RIGHT: new SIG, or the selected SIG and the generics tagged to it */}
                    <div className="groups-settings__detail">
                        {sigEditing === 'new' ? (
                            <>
                                <div className="groups-settings__detail-head">
                                    <h3>New SIG</h3>
                                </div>
                                <div className="generic-sig-settings__picker">
                                    <Select
                                        mode="multiple"
                                        size="large"
                                        value={newSigGenerics}
                                        placeholder="Generics this SIG is for (search the medicine list)…"
                                        filterOption={false}
                                        onSearch={handleGenericSearch}
                                        onChange={(values) => {
                                            setNewSigGenerics(values);
                                            resetGenericSearch();
                                        }}
                                        options={genericOptions.map((generic) => ({ value: generic, label: generic }))}
                                        notFoundContent={genericNotFound}
                                    />
                                </div>
                                {renderEditor({
                                    withPower: false,
                                    saveLabel: 'Save',
                                    canSave: !!sig.trim() && newSigGenerics.length > 0,
                                    onSave: handleSaveSig,
                                    onCancel: () => setSigEditing(null),
                                })}
                            </>
                        ) : !selectedSig ? (
                            <div className="groups-settings__empty groups-settings__detail-empty">
                                <FontAwesomeIcon icon={faPills} />
                                <span>Select a SIG to see its generics, or create a new one.</span>
                            </div>
                        ) : (
                            <>
                                <div className="groups-settings__detail-head">
                                    <h3>Generics using this SIG</h3>
                                    <span className="groups-settings__count">{sigGenericCount}</span>
                                </div>

                                {sigEditing === 'edit' ? (
                                    <>
                                        <p className="generic-sig-settings__hint">
                                            Changes apply to all {sigGenericCount} generic{sigGenericCount === 1 ? '' : 's'} below.
                                        </p>
                                        {renderEditor({
                                            withPower: false,
                                            saveLabel: 'Update',
                                            canSave: !!sig.trim(),
                                            onSave: handleSaveSig,
                                            onCancel: () => setSigEditing(null),
                                        })}
                                    </>
                                ) : (
                                    <div className="generic-sig-settings__card generic-sig-settings__card--lead">
                                        <div className="generic-sig-settings__card-body">
                                            <div className="generic-sig-settings__card-meta">
                                                <span className="generic-sig-settings__form">{selectedSig.form || 'Any form'}</span>
                                                {(selectedSig.language || sigRows[0]?.language) === 'bangla' && <span className="generic-sig-settings__lang">বাংলা</span>}
                                                {sigUsage > 0 && <span className="generic-sig-settings__usage">used {sigUsage}×</span>}
                                            </div>
                                            <div className="generic-sig-settings__sig">{selectedSig.sig}</div>
                                            {selectedSig.duration && <div className="generic-sig-settings__duration">Duration: {formatDuration(selectedSig.duration)}</div>}
                                        </div>
                                        <div className="groups-settings__actions" style={{ opacity: 1 }}>
                                            <button type="button" className="groups-settings__icon-btn" title="Edit for all its generics" onClick={() => openEditSig(selectedSig)}>
                                                <FontAwesomeIcon icon={faPen} />
                                            </button>
                                            <button
                                                type="button"
                                                className="groups-settings__icon-btn is-danger"
                                                title="Delete from all its generics"
                                                onClick={() => setConfirm({
                                                    title: 'Delete SIG?',
                                                    text: `“${selectedSig.sig}” will be removed from all ${sigGenericCount} generic${sigGenericCount === 1 ? '' : 's'}. This can’t be undone.`,
                                                    confirmLabel: 'Delete',
                                                    onConfirm: () => handleDeleteSigGroup(selectedSig),
                                                })}
                                            >
                                                <FontAwesomeIcon icon={faTrash} />
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {/* generic tags */}
                                <div className="generic-sig-settings__tags">
                                    {sigRows.map((row) => (
                                        <span key={row._id} className="generic-sig-settings__tag" title={`${row.generic}${row.power ? ` · ${row.power}` : ''}`}>
                                            <span className="generic-sig-settings__tag-text">{row.generic}{row.power ? ` · ${row.power}` : ''}</span>
                                            <button
                                                type="button"
                                                title="Remove this generic"
                                                onClick={() => setConfirm({
                                                    title: 'Remove generic?',
                                                    text: `This SIG will no longer be offered for ${row.generic}${row.power ? ` (${row.power})` : ''}.`,
                                                    confirmLabel: 'Remove',
                                                    onConfirm: () => handleUntag(row),
                                                })}
                                            >
                                                <FontAwesomeIcon icon={faXmark} />
                                            </button>
                                        </span>
                                    ))}
                                </div>

                                <div className="generic-sig-settings__picker">
                                    <Select
                                        showSearch
                                        size="large"
                                        value={null}
                                        placeholder="Tag another generic (search the medicine list)…"
                                        filterOption={false}
                                        searchValue={genericQuery}
                                        onSearch={handleGenericSearch}
                                        onChange={handleTagGeneric}
                                        options={genericOptions
                                            .filter((generic) => !sigRows.some((row) => row.generic === generic))
                                            .map((generic) => ({ value: generic, label: generic }))}
                                        notFoundContent={genericNotFound}
                                    />
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            <CustomModal size="sm" show={!!confirm} setShow={() => setConfirm(null)} isHeaderHide={true}>
                <div className="groups-settings__confirm">
                    <h4>{confirm?.title}</h4>
                    <p>{confirm?.text}</p>
                    <div className="groups-settings__confirm-actions">
                        <button type="button" onClick={() => setConfirm(null)}>Cancel</button>
                        <button
                            type="button"
                            className="danger"
                            onClick={() => {
                                confirm?.onConfirm?.();
                                setConfirm(null);
                            }}
                        >
                            {confirm?.confirmLabel || 'Delete'}
                        </button>
                    </div>
                </div>
            </CustomModal>
        </>
    );
};

export default GenericSigManager;
